/**
 * EventMotionPlayer — scrub through one person-episode's photos as
 * if they were a stop-motion clip. Mirrors the camera4 photo
 * viewer's "watch the customer walk up to the box" UX: photos are
 * pre-sorted oldest -> newest, the player shows one frame at a
 * time with a scrubber + play/pause + timeline, and clicking the
 * frame opens the full blob in a new tab.
 *
 * Each event has its own player instance — the parent
 * (PhotosViewer) toggles between "grid" and "player" mode per
 * event so an operator can keep some collapsed while scrubbing
 * another.
 *
 * Playback rate auto-fits the episode duration into ~6 seconds of
 * playback (clamped to 2-15 fps) so a 30s checkout doesn't take
 * 30s to watch, but a 2s episode isn't a blur either.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { BoxPhoto } from "../services/boxDetailApi";
import { fmtUtc } from "../lib/format";
import { Button } from "./ui/button";

interface Props {
  photos: BoxPhoto[];
  /** Optional episode duration in seconds — used to pick an
   *  auto-fit playback rate. Falls back to a sensible default. */
  durationSeconds?: number | null;
}

function pickFps(photos: number, durationSeconds: number | null | undefined): number {
  // Aim to play the whole sequence in 5–8 seconds.
  const targetSeconds = Math.min(8, Math.max(5, photos / 2));
  const fps = photos / targetSeconds;
  return Math.min(15, Math.max(2, fps));
}

export const EventMotionPlayer: React.FC<Props> = ({ photos, durationSeconds }) => {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const timerRef = useRef<number | null>(null);

  const fps = useMemo(() => pickFps(photos.length, durationSeconds), [photos.length, durationSeconds]);
  const frameMs = useMemo(() => Math.round(1000 / fps), [fps]);

  // Reset to first frame when the photo set changes (different event).
  useEffect(() => {
    setIndex(0);
    setPlaying(false);
  }, [photos]);

  // Playback timer — advances index every `frameMs`; auto-pauses at end.
  useEffect(() => {
    if (!playing) return;
    if (photos.length <= 1) {
      setPlaying(false);
      return;
    }
    timerRef.current = window.setInterval(() => {
      setIndex((i) => {
        const next = i + 1;
        if (next >= photos.length) {
          setPlaying(false);
          return photos.length - 1;
        }
        return next;
      });
    }, frameMs);
    return () => {
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [playing, frameMs, photos.length]);

  const togglePlay = useCallback(() => {
    if (photos.length <= 1) return;
    // From the end? Restart.
    setPlaying((wasPlaying) => {
      const willPlay = !wasPlaying;
      if (willPlay && index >= photos.length - 1) {
        setIndex(0);
      }
      return willPlay;
    });
  }, [photos.length, index]);

  // Preload the next 2 images so playback doesn't stutter as we
  // walk into untouched URLs.
  useEffect(() => {
    for (let k = 1; k <= 2; k++) {
      const p = photos[index + k];
      if (p?.azure_url) {
        const img = new Image();
        img.src = p.azure_url;
      }
    }
  }, [photos, index]);

  if (photos.length === 0) {
    return (
      <div className="p-3 text-xs italic text-secondary-500">
        No AI-confirmed-person photos in window
        {" "}(camera saw a person but no individual frame passed the AI threshold).
      </div>
    );
  }

  const current = photos[Math.min(index, photos.length - 1)];

  return (
    <div className="p-3">
      {/* Big frame */}
      <a
        href={current.azure_url}
        target="_blank"
        rel="noreferrer"
        className="block group"
        title={`${current.camera_name ?? "camera"} · ${fmtUtc(current.timestamp)} · open full size`}
      >
        <img
          src={current.azure_url}
          alt={`${current.camera_name ?? "camera"} ${fmtUtc(current.timestamp)}`}
          loading="eager"
          className="w-full max-h-[480px] object-contain rounded border border-secondary-200 bg-secondary-50"
        />
      </a>

      {/* Caption */}
      <div className="mt-1.5 flex items-baseline justify-between text-[11px] text-secondary-600">
        <span>
          Frame <span className="font-semibold">{index + 1}</span> / {photos.length}
          {current.camera_name && (
            <span className="ml-2 text-secondary-500">{current.camera_name}</span>
          )}
        </span>
        <span>{fmtUtc(current.timestamp)}</span>
      </div>

      {/* Controls */}
      <div className="mt-2 flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          onClick={togglePlay}
          disabled={photos.length <= 1}
          title={playing ? "Pause" : photos.length <= 1 ? "Single frame — nothing to play" : "Play sequence"}
        >
          {playing ? "❚❚ Pause" : "▶ Play"}
        </Button>
        <input
          type="range"
          min={0}
          max={Math.max(0, photos.length - 1)}
          value={index}
          onChange={(e) => { setIndex(Number(e.target.value)); setPlaying(false); }}
          className="flex-1 accent-primary-600"
          aria-label="Scrub"
        />
        <span className="text-[11px] text-secondary-500 w-12 text-right" title={`${fps.toFixed(1)} fps`}>
          {fps.toFixed(1)} fps
        </span>
      </div>

      {/* Thumbnail filmstrip — click to jump */}
      {photos.length > 1 && (
        <div className="mt-2 grid grid-flow-col auto-cols-[64px] gap-1 overflow-x-auto pb-1">
          {photos.map((p, i) => (
            <button
              key={`${p.azure_url}:${i}`}
              type="button"
              onClick={() => { setIndex(i); setPlaying(false); }}
              className={
                "block rounded overflow-hidden border " +
                (i === index
                  ? "border-primary-500 ring-2 ring-primary-200"
                  : "border-secondary-200 hover:border-secondary-400")
              }
              title={`Frame ${i + 1} · ${fmtUtc(p.timestamp)}`}
            >
              <img
                src={p.azure_url}
                alt={`Frame ${i + 1}`}
                loading="lazy"
                className="w-16 h-12 object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventMotionPlayer;
